import { Link } from "react-router-dom";
import { ArrowRight, Battery, Compass, Shield, Sun, Wrench, ClipboardCheck, HeadphonesIcon, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchPackages } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG = "https://images.unsplash.com/photo-1644769623789-3ba315c54670?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Beranda() {
  const [packages, setPackages] = useState([]);
  useEffect(() => { fetchPackages().then((d) => setPackages(d.packages || [])).catch(() => {}); }, []);

  const samplePkgs = ["pkg-3500-starter", "pkg-3500-safer", "pkg-3500-signature"]
    .map((id) => packages.find((p) => p.id === id)).filter(Boolean);

  return (
    <div data-testid="page-beranda">
      {/* Hero */}
      <section className="pe-grain">
        <div className="pe-container pt-12 lg:pt-20 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 pe-reveal">
            <div className="pe-eyebrow">PLTS Tumbuh · Secure Energy Living</div>
            <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.1]">
              PLTS yang direncanakan untuk kebutuhan rumah hari ini.
              <span className="block" style={{ color: "var(--pe-primary)" }}>Siap tumbuh untuk kebutuhan berikutnya.</span>
            </h1>
            <p className="mt-6 text-lg text-[color:var(--pe-text-muted)] max-w-2xl">
              Kenali kebutuhan listrik rumah, bandingkan pilihan sistem, dan rencanakan investasi PLTS bersama Proper Energy.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/simulasi" className="pe-btn-primary" data-testid="hero-cta-guided">
                Cari PLTS yang Sesuai <ArrowRight size={18} />
              </Link>
              <Link to="/konfigurator" className="pe-btn-ghost" data-testid="hero-cta-direct">
                Atur Konfigurasi Sendiri
              </Link>
            </div>
            <p className="mt-4 text-xs text-[color:var(--pe-text-muted)]">Simulasi awal tanpa perlu membuat akun.</p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: "4/3" }}>
              <img src={HERO_IMG} alt="Rumah tropis dengan PLTS" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 right-4 bg-white rounded-xl p-4 shadow-lg max-w-[220px]">
                <div className="pe-eyebrow text-[10px]">Pratinjau simulasi</div>
                <div className="text-xs text-[color:var(--pe-text-muted)] mt-1">Masukkan daya PLN & kebutuhan, dapatkan 3 opsi dalam ~2 menit.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two paths */}
      <section className="pe-section bg-[color:var(--pe-bg-soft)]">
        <div className="pe-container">
          <div className="pe-eyebrow">Mulai dari mana?</div>
          <h2 className="mt-2 font-display font-bold text-3xl lg:text-4xl">Dua jalur, satu hasil yang bisa dibandingkan</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Link to="/simulasi" className="pe-card group" data-testid="path-guided">
              <Compass className="w-10 h-10 mb-4" style={{ color: "var(--pe-primary)" }} />
              <h3 className="font-display font-semibold text-xl">Saya ingin dibantu memilih</h3>
              <p className="mt-2 text-[color:var(--pe-text-muted)]">
                Lima langkah ringan: data rumah, pola pemakaian, prioritas backup, kondisi atap. Hasil: tiga opsi Starter, Safer, Signature yang dapat dibandingkan.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--pe-primary)" }}>Mulai simulasi <ArrowRight size={16} /></div>
            </Link>
            <Link to="/konfigurator" className="pe-card group" data-testid="path-direct">
              <Wrench className="w-10 h-10 mb-4" style={{ color: "var(--pe-primary)" }} />
              <h3 className="font-display font-semibold text-xl">Saya sudah tahu konfigurasi yang dicari</h3>
              <p className="mt-2 text-[color:var(--pe-text-muted)]">
                Pilih paket, lalu sesuaikan jumlah panel atau baterai. Validasi kompatibilitas dan estimasi berjalan otomatis.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--pe-primary)" }}>Buka konfigurator <ArrowRight size={16} /></div>
            </Link>
          </div>
        </div>
      </section>

      {/* PLTS Tumbuh concept */}
      <section className="pe-section">
        <div className="pe-container">
          <div className="pe-eyebrow">Konsep PLTS Tumbuh</div>
          <h2 className="mt-2 font-display font-bold text-3xl lg:text-4xl max-w-3xl">Mulai sesuai kemampuan, tumbuh sesuai kebutuhan</h2>
          <p className="mt-4 text-[color:var(--pe-text-muted)] max-w-3xl">
            Fondasi sistem yang sama untuk Starter, Safer, dan Signature. Yang berbeda adalah kapasitas, cakupan kebutuhan, dan ketahanan backup — bukan standar keselamatan atau kualitas pengerjaan.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { icon: Sun, title: "Tahap 1 — Starter", desc: "Titik mulai paling terjangkau yang memenuhi prioritas minimum rumah." },
              { icon: Battery, title: "Tahap 2 — Safer", desc: "Peningkatan ketahanan energi dengan tambahan baterai sesuai pola pemakaian." },
              { icon: Shield, title: "Tahap 3 — Signature", desc: "Cakupan kebutuhan lebih tinggi dan backup lebih panjang dalam kondisi rumah yang mendukung." },
            ].map((s, i) => (
              <div key={i} className="pe-card">
                <s.icon className="w-9 h-9 mb-3" style={{ color: "var(--pe-primary)" }} />
                <h3 className="font-display font-semibold text-lg">{s.title}</h3>
                <p className="mt-2 text-[color:var(--pe-text-muted)]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 package preview */}
      {samplePkgs.length === 3 && (
        <section className="pe-section bg-[color:var(--pe-bg-soft)]">
          <div className="pe-container">
            <div className="pe-eyebrow">Contoh untuk PLN 3.500 VA</div>
            <h2 className="mt-2 font-display font-bold text-3xl lg:text-4xl">Tiga pilihan, kualitas pengerjaan setara</h2>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {samplePkgs.map((p) => {
                const variant = p.tier === "Starter" ? "pkg-starter" : p.tier === "Safer" ? "pkg-safer" : "pkg-signature";
                const isDark = p.tier === "Signature";
                return (
                  <div key={p.id} className={`pe-card ${variant}`} data-testid={`home-pkg-${p.tier.toLowerCase()}`}>
                    <div className={`pe-eyebrow ${isDark ? "!text-cyan-300" : ""}`}>{p.tier}</div>
                    <div className={`mt-2 font-display font-bold text-2xl ${isDark ? "text-white" : ""}`}>{p.kwp} kWp</div>
                    <ul className={`mt-4 space-y-1.5 text-sm ${isDark ? "text-slate-200" : "text-[color:var(--pe-text-muted)]"}`}>
                      <li>{p.panels} panel × {p.panel_wp} Wp</li>
                      <li>Inverter {p.inverter_kw} kW</li>
                      <li>Baterai {p.battery_units} × {p.battery_kwh_per_unit} kWh ({p.battery_nominal_kwh.toFixed(2)} kWh nominal)</li>
                    </ul>
                    <div className={`mt-5 font-mono-spec text-sm ${isDark ? "text-cyan-200" : ""}`}>Harga referensi: Rp {p.price_rp.toLocaleString("id-ID")}</div>
                    <Link to="/simulasi" className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${isDark ? "text-cyan-300" : ""}`} style={!isDark ? { color: "var(--pe-primary)" } : {}}>
                      Simulasikan untuk rumah saya <ArrowRight size={16} />
                    </Link>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-xs text-[color:var(--pe-text-muted)]">
              Harga referensi 1 fase wilayah Jabodetabek, mencakup installation & commissioning menurut sumber brosur v1.0 Juni 2026. Harga, pajak, dan ketentuan perlu diverifikasi admin sebelum penawaran final.
            </p>
          </div>
        </section>
      )}

      {/* Pre-install planning */}
      <section className="pe-section">
        <div className="pe-container">
          <div className="pe-eyebrow">Direncanakan sebelum instalasi</div>
          <h2 className="mt-2 font-display font-bold text-3xl lg:text-4xl max-w-3xl">Yang kami cek sebelum memasang panel di atap</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { t: "Beban rumah", d: "Pola siang/malam, beban puncak, dan beban penting yang perlu tetap menyala." },
              { t: "Kondisi atap", d: "Luas yang bisa dipakai, arah bidang, bayangan, dan kesiapan struktur." },
              { t: "Proteksi listrik", d: "MCB/MCCB AC & DC, SPD, grounding, dan isolator sesuai desain." },
              { t: "Backup & reserve", d: "SOC minimum, cadangan untuk pemadaman, dan batas daya inverter." },
              { t: "Ruang pengembangan", d: "Jalur kabel, kapasitas inverter, dan slot baterai untuk tahap berikut." },
              { t: "Pengerjaan & monitoring", d: "Pemasangan rapi, commissioning terdokumentasi, dan pendampingan pascapasang." },
            ].map((it, i) => (
              <div key={i} className="pe-card">
                <h3 className="font-display font-semibold text-lg">{it.t}</h3>
                <p className="mt-2 text-[color:var(--pe-text-muted)]">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flow */}
      <section className="pe-section bg-[color:var(--pe-navy)] text-white">
        <div className="pe-container">
          <div className="pe-eyebrow !text-cyan-300">Alur bersama Proper Energy</div>
          <h2 className="mt-2 font-display font-bold text-3xl lg:text-4xl text-white">Dari konsultasi sampai pendampingan</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Compass, t: "Konsultasi", d: "Pahami kebutuhan dan pola pemakaian rumah." },
              { icon: ClipboardCheck, t: "Survei & desain", d: "Pengecekan atap, beban, dan rancangan sistem." },
              { icon: Wrench, t: "Instalasi & commissioning", d: "Pemasangan rapi, pengujian, dan serah terima." },
              { icon: HeadphonesIcon, t: "Pendampingan", d: "Monitoring awal, perawatan, dan rencana tumbuh." },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-white/15 p-5 bg-white/5">
                <s.icon className="w-8 h-8 mb-3 text-cyan-300" />
                <h3 className="font-display font-semibold text-lg text-white">{s.t}</h3>
                <p className="mt-2 text-slate-300 text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pe-section">
        <div className="pe-container max-w-3xl">
          <div className="pe-eyebrow">FAQ</div>
          <h2 className="mt-2 font-display font-bold text-3xl lg:text-4xl">Pertanyaan yang sering ditanyakan</h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="home-faq">
            <AccordionItem value="q1">
              <AccordionTrigger>Apakah PLTS Proper Energy pasti membuat tagihan PLN nol?</AccordionTrigger>
              <AccordionContent>Tidak otomatis. Besarnya penghematan bergantung pada konsumsi, pola siang/malam, kapasitas yang dipasang, dan asumsi tarif. Simulasi kami menampilkan estimasi berdasarkan metode terdokumentasi — bukan janji.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="q2">
              <AccordionTrigger>Berapa lama backup saat listrik padam?</AccordionTrigger>
              <AccordionContent>Durasi backup bergantung pada kapasitas baterai, SOC saat pemadaman, efisiensi discharge, dan beban yang menyala. Hasil simulasi menampilkan contoh perhitungan dengan asumsi yang jelas.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="q3">
              <AccordionTrigger>Bisakah Starter nanti di-upgrade ke Safer atau Signature?</AccordionTrigger>
              <AccordionContent>Bisa, selama batas inverter, susunan panel, baterai, atap, dan desain instalasi memungkinkan. Rencana Tumbuh membantu memperlihatkan skenario pengembangan beserta batasnya.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="q4">
              <AccordionTrigger>Apa yang termasuk dalam harga referensi?</AccordionTrigger>
              <AccordionContent>Harga referensi di tabel mengacu pada sumber brosur v1.0 Juni 2026 untuk Jabodetabek, mencakup installation & commissioning. Status pajak, biaya tambahan, serta item pendukung belum semua dinyatakan lengkap. Penawaran final mengikuti hasil survei.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pe-section bg-[color:var(--pe-bg-soft)]">
        <div className="pe-container text-center">
          <Zap className="w-10 h-10 mx-auto" style={{ color: "var(--pe-primary)" }} />
          <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl">Siap merancang PLTS untuk rumah Bapak/Ibu?</h2>
          <p className="mt-4 text-[color:var(--pe-text-muted)] max-w-xl mx-auto">Mulai dari simulasi ringan atau langsung berkonsultasi dengan tim kami.</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/simulasi" className="pe-btn-primary" data-testid="footer-cta-simulasi">Mulai Simulasi <ArrowRight size={18} /></Link>
            <Link to="/kontak" className="pe-btn-ghost" data-testid="footer-cta-konsultasi">Konsultasi Langsung</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
